import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";
import HintButton from "@/components/HintButton";
import SuppliersModal from "@/components/finance/SuppliersModal";
import BillsCalendar from "@/components/finance/BillsCalendar";
import BillsExportPanel from "@/components/finance/BillsExportPanel";
import BillsTable from "@/components/finance/BillsTable";
import BillFormModal from "@/components/finance/BillFormModal";
import PayBillModal from "@/components/finance/PayBillModal";
import { emptyBill, iso } from "@/components/finance/apShared";

export default function AccountsPayable() {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [bills, setBills] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showBill, setShowBill] = useState(null); // { form, editingId }
  const [showSuppliers, setShowSuppliers] = useState(false);
  const [showPay, setShowPay] = useState(null); // {bill, form}
  const [error, setError] = useState("");

  const monthStart = useMemo(() => new Date(cursor.getFullYear(), cursor.getMonth(), 1), [cursor]);
  const monthEnd = useMemo(() => new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0), [cursor]);

  const load = async () => {
    try {
      const params = { start: iso(monthStart), end: iso(monthEnd) };
      const [{ data: b }, { data: s }] = await Promise.all([
        api.get("/bills", { params }),
        api.get("/suppliers"),
      ]);
      setBills(b);
      setSuppliers(s);
    } catch (err) {
      console.warn("AccountsPayable load:", err?.response?.status || err?.message);
    }
  };

  useEffect(() => { load(); }, [cursor]); // eslint-disable-line react-hooks/exhaustive-deps

  const billsByDay = useMemo(() => {
    const map = {};
    for (const b of bills) {
      const day = b.due_date;
      if (!map[day]) map[day] = [];
      map[day].push(b);
    }
    return map;
  }, [bills]);

  const monthTotal = useMemo(() => bills.filter((b) => b.status === "pending").reduce((s, b) => s + Number(b.amount || 0), 0), [bills]);
  const paidTotal = useMemo(() => bills.filter((b) => b.status === "paid").reduce((s, b) => s + Number(b.amount || 0), 0), [bills]);
  // Sort não-mutante do array de state (usar `bills.sort(...)` direto na JSX
  // muta o array de state, o que quebra o React em edge cases).
  const sortedBills = useMemo(
    () => [...bills].sort((a, b) => a.due_date.localeCompare(b.due_date)),
    [bills],
  );

  // Grid do calendário
  const grid = useMemo(() => {
    const days = [];
    const firstWd = monthStart.getDay();
    for (let i = 0; i < firstWd; i++) days.push(null); // preenchimento
    const lastDay = monthEnd.getDate();
    for (let d = 1; d <= lastDay; d++) {
      const dt = new Date(cursor.getFullYear(), cursor.getMonth(), d);
      days.push(dt);
    }
    while (days.length % 7 !== 0) days.push(null);
    return days;
  }, [cursor, monthStart, monthEnd]);

  const openNewBill = (dt) => {
    setShowBill({ form: { ...emptyBill, due_date: iso(dt) }, editingId: null });
    setError("");
  };

  const openEditBill = (b) => {
    setShowBill({
      form: {
        supplier_id: b.supplier_id || "",
        supplier_name: b.supplier_name || "",
        amount: b.amount,
        due_date: b.due_date,
        payment_method: b.payment_method,
        description: b.description || "",
        category: b.category || "Geral",
        recurring: b.recurring || "none",
        recurring_end: b.recurring_end || "",
      },
      editingId: b.id,
    });
    setError("");
  };

  const saveBill = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { ...showBill.form, amount: parseFloat(showBill.form.amount) || 0 };
      if (!payload.amount || payload.amount <= 0) { setError("Valor deve ser maior que zero"); return; }
      if (payload.supplier_id === "") payload.supplier_id = null;
      if (!payload.recurring_end) payload.recurring_end = null;
      if (showBill.editingId) {
        await api.put(`/bills/${showBill.editingId}`, payload);
        toast.success("Conta atualizada");
      } else {
        const { data } = await api.post("/bills", payload);
        toast.success(data.created > 1 ? `${data.created} lançamentos criados (recorrência)` : "Conta cadastrada");
      }
      setShowBill(null);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const openPay = (b) => {
    setShowPay({ bill: b, form: { paid_date: iso(new Date()), payment_method: b.payment_method, notes: "" } });
  };

  const savePayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/bills/${showPay.bill.id}/pay`, showPay.form);
      setShowPay(null);
      toast.success("Pagamento registrado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  const unpayBill = async (b) => {
    if (!confirm("Reverter pagamento desta conta?")) return;
    await api.post(`/bills/${b.id}/unpay`);
    toast.success("Pagamento revertido");
    load();
  };

  const delBill = async (b) => {
    const isRecurring = b.parent_bill_id || b.recurring !== "none";
    let deleteSeries = false;
    if (isRecurring) {
      deleteSeries = confirm("Este lançamento faz parte de uma série recorrente. OK = remover a série toda · Cancelar = remover só este mês");
    } else {
      if (!confirm("Remover esta conta?")) return;
    }
    await api.delete(`/bills/${b.id}${deleteSeries ? "?delete_series=true" : ""}`);
    toast.success("Conta removida");
    load();
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="overline text-zinc-500 mb-2">FINANCEIRO</div>
          <h1 className="font-head text-2xl sm:text-3xl font-black tracking-tight">Contas a Pagar</h1>
          <HintButton text="Clique num dia vazio para lançar uma conta · Clique num lançamento pendente para dar baixa (marcar como pago)." />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button className="btn btn-ghost" onClick={() => setShowSuppliers(true)} data-testid="open-suppliers"><Buildings size={16} weight="bold" /> Fornecedores</button>
          <button className="btn" onClick={() => openNewBill(new Date())} data-testid="new-bill"><Plus size={16} weight="bold" /> Nova conta</button>
        </div>
      </div>

      <BillsCalendar
        cursor={cursor}
        setCursor={setCursor}
        grid={grid}
        billsByDay={billsByDay}
        monthTotal={monthTotal}
        paidTotal={paidTotal}
        onNewBill={openNewBill}
        onPay={openPay}
        onEdit={openEditBill}
      />

      <BillsExportPanel />

      <BillsTable
        bills={bills}
        sortedBills={sortedBills}
        cursor={cursor}
        onPay={openPay}
        onUnpay={unpayBill}
        onEdit={openEditBill}
        onDelete={delBill}
      />

      {showBill && (
        <BillFormModal
          showBill={showBill}
          setShowBill={setShowBill}
          suppliers={suppliers}
          error={error}
          onSubmit={saveBill}
        />
      )}

      {showSuppliers && (
        <SuppliersModal suppliers={suppliers} onClose={() => { setShowSuppliers(false); load(); }} />
      )}

      {showPay && (
        <PayBillModal showPay={showPay} setShowPay={setShowPay} onSubmit={savePayment} />
      )}
    </div>
  );
}
